import { RuleValidationTargetType } from '@atlas/types'
import { CardShell, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toRuleListItems } from '@/adapters/rule-list-item'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { BackLink } from '@/components/editorial/BackLink'
import { AttributesPanel } from '@/components/library/AttributesPanel'
import { FileClassificationSection } from '@/components/library/FileClassificationSection'
import { UsedInPanel } from '@/components/library/UsedInPanel'
import { VersionHistoryPanel } from '@/components/library/VersionHistoryPanel'
import { ApplicableRulesTabs } from '@/components/rules/ApplicableRulesTabs'
import { AxisCode, findAttributeLabel } from '@/domain/asset-detail.types'
import { useAssetDetail, useDeleteFile, useDownloadFile, useFileGenericAttributes } from '@/hooks/useAssetDetail'
import { useFileApplicableRules, useFileUsage, useFileVersions } from '@/hooks/useAssetDetailExtras'
import { useAuth } from '@/hooks/useAuth'
import { useRuleEditing } from '@/hooks/useRuleEditing'
import { useThumbnails } from '@/hooks/useThumbnails'
import { canDeleteOwnedBy } from '@/lib/capabilities'
import { REFUSAL, Restricted } from '@/lib/refusal'

/** The asset's two irreversible-ish actions. Download owns its own mutation; delete only asks,
 *  because the confirmation and the navigation that follows it belong to the page. */
function AssetActions({
	fileId,
	isDeleting,
	onRequestDelete,
	canDelete,
}: {
	fileId: string
	isDeleting: boolean
	onRequestDelete: () => void
	canDelete: boolean
}) {
	const downloadFile = useDownloadFile()

	return (
		<>
			<Button
				className="w-full bg-ds-text text-ds-background hover:opacity-90 disabled:opacity-50"
				isDisabled={downloadFile.isPending}
				onPress={() => downloadFile.mutate(fileId)}
			>
				{downloadFile.isPending ? 'Preparing download…' : '↓ Download source'}
			</Button>
			{downloadFile.isError && <p className="text-sm text-ds-danger">Download failed — please try again.</p>}
			<Restricted allowed={canDelete} why={REFUSAL.deleteFile} className="w-full">
				<Button variant="danger" className="w-full" isDisabled={isDeleting} onPress={onRequestDelete}>
					{isDeleting ? 'Deleting…' : 'Delete asset'}
				</Button>
			</Restricted>
		</>
	)
}

/** Asset Detail (design §8.10, WF1d): preview/classification/attributes are REAL
 * (same file-service data the Library grid uses); version history, applicable
 * rules, and "used in" are MOCK stand-ins with no backend anywhere today. */
export function AssetDetailPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { file, isLoading } = useAssetDetail(id ?? null)
	const { user } = useAuth()
	const thumbnails = useThumbnails(file ? [file] : [])
	const { data: versions = [] } = useFileVersions(id ?? null)
	const { data: usage = [] } = useFileUsage(id ?? null)
	const { data: applicableRules = [], isError: applicableRulesError } = useFileApplicableRules(id ?? null)
	const { data: genericAttributes = [] } = useFileGenericAttributes(id ?? null)
	const { canEdit, editRule, ruleHref, openRule } = useRuleEditing()
	const deleteFile = useDeleteFile()
	const [confirmingDelete, setConfirmingDelete] = useState(false)

	if (isLoading) return <ListSkeleton label="Loading…" className="py-16" />
	if (!file) return <p className="py-16 text-center text-sm text-ds-text-subtle">File not found.</p>

	const thumbnailUrl = thumbnails[file.fileId] ?? null

	return (
		<div className="space-y-6">
			<BackLink fallbackHref="/assets" />
			<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">Library / {file.fileName}</p>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
				<div className="space-y-6">
					<CardShell as="section" className="flex aspect-video items-center justify-center bg-ds-surface p-0">
						{thumbnailUrl ? (
							<img src={thumbnailUrl} alt={file.fileName} className="h-full w-full object-contain" />
						) : (
							<span className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">
								Preview · {file.fileName}
							</span>
						)}
					</CardShell>
					{/* The shared applicable-rules card. No display code: `IRule` carries a uuid, and
					    a uuid on a card is an identifier the reader cannot use or look up. */}
					<ApplicableRulesTabs
						targetType={RuleValidationTargetType.FILE}
						targetId={file.fileId}
						rules={toRuleListItems(applicableRules)}
						title="Applicable rules · computed live"
						isError={applicableRulesError}
						emptyLabel="No rules matched."
						canEdit={canEdit}
						onEditRule={editRule}
						ruleHref={ruleHref}
						onOpenRule={openRule}
						note={canEdit ? 'Editing a rule changes it everywhere it resolves, not only on this asset.' : undefined}
					/>
					<VersionHistoryPanel versions={versions} />
				</div>

				<div className="space-y-6">
					<AssetActions
						fileId={file.fileId}
						isDeleting={deleteFile.isPending}
						onRequestDelete={() => setConfirmingDelete(true)}
						canDelete={canDeleteOwnedBy(user?.role, user?.userId, file.uploadedBy)}
					/>
					<FileClassificationSection fileId={file.fileId} tags={file.tags} />
					<AttributesPanel
						licence={findAttributeLabel(file.tags, AxisCode.LICENSE)}
						checksum={file.checksum}
						fileSize={file.fileSize}
						genericAttributes={genericAttributes}
					/>
					<UsedInPanel usage={usage} />
				</div>
			</div>

			{/* Navigates away on success rather than patching a cache: the asset this page is
			    about no longer exists, so there is nothing left here to render. */}
			<ConfirmDialog
				open={confirmingDelete}
				title="Delete this asset?"
				description={`${file.fileName} and its classification will be removed. This cannot be undone.`}
				confirmLabel="Delete asset"
				confirmVariant="danger"
				busyLabel="Deleting…"
				submitting={deleteFile.isPending}
				onCancel={() => setConfirmingDelete(false)}
				onConfirm={() =>
					deleteFile.mutate(file.fileId, {
						onSuccess: () => {
							setConfirmingDelete(false)
							void navigate('/assets')
						},
						// Stays open on failure, so the retry sits next to the reason.
						onError: () => setConfirmingDelete(true),
					})
				}
			/>
		</div>
	)
}
